import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import Stories from '../components/Stories';
import {
  ClipboardCheck, GraduationCap, Brain, ChevronRight, ChevronLeft, ChevronDown,
  BarChart3, Target, Users, Building2, Lightbulb, DollarSign,
  Bell, Star, Zap, Ticket, Upload, Newspaper, Calculator, Trophy,
  MessageCircle, BookOpen, Timer, Sparkles
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTMENTS = [
  {
    id: 'operations', name: 'Operations & Audits',
    description: 'Daily Excellence',
    icon: ClipboardCheck, gradient: 'from-orange-500 via-amber-500 to-orange-400',
    roles: ['owner', 'area_manager', 'manager', 'employee', 'supervisor'],
    subItems: [
      { label: 'Audits', path: '/audits', icon: ClipboardCheck, desc: 'Daily walk-thrus & assessments' },
      { label: 'Reports', path: '/reports', icon: BarChart3, desc: 'Compliance & performance reports', roles: ['owner', 'area_manager', 'manager'] },
      { label: 'Service Tickets', path: '/service-tickets', icon: Ticket, desc: 'Submit & track issues' },
      { label: 'Quick Tasks', path: '/quick-tasks', icon: Zap, desc: 'Assign & manage tasks', roles: ['owner', 'area_manager', 'manager', 'supervisor'] },
    ]
  },
  {
    id: 'finance', name: 'Finance & KPIs',
    description: 'Profitability',
    icon: DollarSign, gradient: 'from-emerald-500 via-green-500 to-emerald-400',
    roles: ['owner', 'area_manager', 'manager'],
    subItems: [
      { label: 'KPI Dashboard', path: '/kpi', icon: Target, desc: 'Weekly performance metrics' },
      { label: 'P&L Report', path: '/profit-loss', icon: Calculator, desc: 'Profit & loss analysis' },
      { label: 'Financial Reports', path: '/financial-reports', icon: BarChart3, desc: 'Executive summary comparisons' },
      { label: 'Service Times', path: '/service-times', icon: Timer, desc: 'Drive-thru speed analysis' },
      { label: 'Year in Review', path: '/yearly-summary', icon: Trophy, desc: 'Annual performance' },
    ]
  },
  {
    id: 'training', name: 'Training & Growth',
    description: 'Leadership Development',
    icon: GraduationCap, gradient: 'from-blue-500 via-indigo-500 to-violet-500',
    roles: ['owner', 'area_manager', 'manager', 'employee', 'supervisor'],
    subItems: [
      { label: 'Resources', path: '/resources', icon: BookOpen, desc: 'Operations manuals & guides' },
      { label: 'Training Tracker', path: '/training-tracker', icon: GraduationCap, desc: 'Station evaluations & progress' },
      { label: 'Best Practices', path: '/best-practices', icon: Target, desc: 'Tips & business insights in one place' },
      { label: 'Rooty (AI)', path: '/ai-assistant', icon: Sparkles, desc: 'A&W AI assistant — chat with your data' },
    ]
  },
  {
    id: 'admin', name: 'Back Office',
    description: 'Administration',
    icon: Building2, gradient: 'from-purple-500 via-violet-500 to-purple-400',
    roles: ['owner', 'area_manager', 'manager'],
    subItems: [
      { label: 'Locations', path: '/stores', icon: Building2, desc: 'Store management' },
      { label: 'Users', path: '/users', icon: Users, desc: 'Team management' },
      { label: 'Messages', path: '/messaging', icon: MessageCircle, desc: 'Team feed & messaging' },
    ]
  },
];

function TipCard({ tip }) {
  const priorityColors = {
    high: 'border-l-red-400 bg-slate-800/80',
    medium: 'border-l-amber-400 bg-slate-800/80',
    low: 'border-l-green-400 bg-slate-800/80'
  };
  return (
    <div className={`min-w-[260px] max-w-[260px] p-4 rounded-xl border border-slate-700/50 border-l-4 ${priorityColors[tip.priority] || priorityColors.medium}`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center shadow-sm">
          <Lightbulb className="w-4 h-4 text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-white truncate">{tip.title}</p>
          <p className="text-xs text-slate-400 mt-1">{tip.category}</p>
          <p className="text-xs text-slate-300 mt-2 line-clamp-2">{tip.content}</p>
        </div>
      </div>
    </div>
  );
}

export default function DepartmentSelector() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ['owner', 'area_manager'].includes(user?.role);
  const visibleDepts = DEPARTMENTS.filter(d => d.roles.includes(user?.role));
  const [tips, setTips] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [expandedDept, setExpandedDept] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [tipsRes, notifRes] = await Promise.all([
        axios.get(`${API_URL}/tips`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/notifications`).catch(() => ({ data: [] }))
      ]);
      setTips(tipsRes.data || []);
      setNotifications(Array.isArray(notifRes.data) ? notifRes.data.filter(n => !n.read) : []);
    } catch (error) { console.error('Error:', error); }
  };

  const scrollTips = (dir) => {
    const c = document.getElementById('tips-container');
    if (c) c.scrollBy({ left: dir * 300, behavior: 'smooth' });
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const handleDeptClick = (deptId) => {
    setExpandedDept(expandedDept === deptId ? null : deptId);
  };

  const unreadNotifications = notifications.filter(n => !n.read);

  return (
    <div className="-m-4 sm:-m-5 lg:-m-8 min-h-screen bg-[#0f172a]" data-testid="department-selector">
      <div className="px-6 pt-8 pb-2">
        <div className="text-center mb-2">
          <p className="text-orange-400 font-semibold text-sm tracking-wide">{getGreeting()}, {user?.name?.split(' ')[0]}</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mt-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Where would you like to go?
          </h1>
          <p className="text-slate-400 mt-2 text-sm">Select a department to get started with your daily tasks</p>
        </div>
        {unreadNotifications.length > 0 && (
          <div className="flex justify-center mt-3">
            <Button variant="outline" size="sm" className="rounded-full bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700" onClick={() => navigate('/notifications')}>
              <Bell className="w-4 h-4 mr-1" />
              {unreadNotifications.length} new
            </Button>
          </div>
        )}
      </div>

      <div className="px-6 py-4">
        <Stories />
      </div>

      {/* Department Cards - Expandable */}
      <div className="px-6 pb-4 space-y-4">
        {visibleDepts.map(dept => {
          const Icon = dept.icon;
          const isExpanded = expandedDept === dept.id;
          const filteredSubItems = dept.subItems.filter(item => !item.roles || item.roles.includes(user?.role));
          return (
            <div key={dept.id} className="rounded-2xl overflow-hidden transition-all duration-300" data-testid={`dept-card-${dept.id}`}>
              {/* Gradient Header - Click to expand */}
              <div
                onClick={() => handleDeptClick(dept.id)}
                className={`bg-gradient-to-r ${dept.gradient} p-6 relative overflow-hidden cursor-pointer group`}
              >
                <div className="absolute -right-6 -top-6 w-32 h-32 bg-white/10 rounded-full" />
                <div className="absolute right-8 bottom-2 w-20 h-20 bg-white/5 rounded-full" />
                <div className="flex items-start justify-between relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <ChevronDown className={`w-6 h-6 text-white/70 group-hover:text-white transition-all duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
                <h2 className="text-xl font-bold text-white mt-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {dept.name}
                </h2>
                <p className="text-white/70 text-sm mt-1">{dept.description}</p>
              </div>

              {/* Expanded Sub-Items */}
              <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="bg-slate-800/80 backdrop-blur-sm border-t border-white/10 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filteredSubItems.map((item, i) => {
                    const ItemIcon = item.icon;
                    return (
                      <button
                        key={i}
                        onClick={() => navigate(item.path)}
                        className="flex items-center gap-3 p-3 rounded-xl bg-slate-700/50 border border-slate-600/50 hover:bg-slate-700 hover:border-slate-500 transition-all text-left group/item"
                        data-testid={`dept-sub-${dept.id}-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${dept.gradient} bg-opacity-20 flex items-center justify-center flex-shrink-0`}>
                          <ItemIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white group-hover/item:text-orange-300 transition-colors">{item.label}</p>
                          <p className="text-xs text-slate-400 mt-0.5 truncate">{item.desc}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-500 group-hover/item:text-white transition-colors flex-shrink-0" />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Collapsed Quick Links Pills */}
              {!isExpanded && (
                <div className="bg-orange-50/90 backdrop-blur-sm px-4 py-3 flex flex-wrap gap-2" style={{ backgroundColor: 'rgba(255,247,237,0.9)' }}>
                  {filteredSubItems.slice(0, 5).map((link, i) => (
                    <button
                      key={i}
                      onClick={(e) => { e.stopPropagation(); navigate(link.path); }}
                      className="text-xs font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 px-3 py-1.5 rounded-full transition-colors shadow-sm"
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Tips Banner removed — replaced by global NewsBar in Layout */}

      {/* Quick Actions - for admins */}
      {isAdmin && (
        <div className="px-6 pb-6">
          <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700/50">
            <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-4">
              <BarChart3 className="w-4 h-4 text-orange-400" />
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Stores', icon: Building2, path: '/stores' },
                { label: 'Service Tickets', icon: Ticket, path: '/service-tickets' },
                { label: 'Daily Audit', icon: ClipboardCheck, path: '/audits' },
                { label: 'Resources', icon: BookOpen, path: '/resources' },
              ].map(action => {
                const AIcon = action.icon;
                return (
                  <button
                    key={action.path}
                    onClick={() => navigate(action.path)}
                    className="flex flex-col items-center justify-center p-4 rounded-xl bg-slate-700/40 border border-slate-600/50 hover:bg-slate-700/60 hover:border-slate-500 transition-all text-slate-300 hover:text-white"
                    data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <AIcon className="w-5 h-5 mb-2" />
                    <span className="text-xs font-medium">{action.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <footer className="px-6 pb-8 pt-2 text-center">
        <p className="text-xs text-slate-500">Created by Mohit Chugh &middot; &copy; 2026 Elmwood Management</p>
      </footer>
    </div>
  );
}
